'use client'

import { useCallback, useEffect, useRef, useState } from 'react'

import { useFeatureSwitch } from '@marketplace-web/shared/feature-switches'
import { UiState } from '@marketplace-web/shared/ui-state-data'

import {
  getClosetPromotions,
  transformClosetPromotionsResponse,
  ClosetModel,
  VasEntryPoint,
} from '@marketplace-web/domain/vas-data'

import { Screen } from 'constants/tracking/screens'

import { useCatalogContext } from 'pages/Catalog/CatalogProvider'
import { useItemsCatalogContext, useItemsCatalogQuery } from 'pages/Catalog/ItemsCatalogProvider'

import useFetchVasEntryPoints from './useFetchVasEntryPoints'

const CLOSET_PROMOTIONS_COUNT = 4

const useClosetPromotion = () => {
  const { uiState } = useItemsCatalogContext()
  const {
    state: { searchSession, filters, dynamicFilters },
  } = useCatalogContext()
  const { page: currentPage } = useItemsCatalogQuery()

  const { selectedDynamicFilters } = dynamicFilters
  const searchSessionId = searchSession.sessionId
  const dynamicFiltersUiState = dynamicFilters.ui.uiState

  const isClosetPromotionKillswitchEnabled = useFeatureSwitch('killswitch_closet_promotion_web')

  const userIds = useRef<Array<number>>([])
  const hasMore = useRef(true)
  const isPaginationEventRef = useRef(false)
  const isFilterChangeEventRef = useRef(false)
  const hasSearchSessionIdChangedRef = useRef(false)

  const [closets, setClosets] = useState<Array<ClosetModel>>([])

  const resetClosets = () => {
    userIds.current = []
    hasMore.current = true
  }

  useEffect(() => {
    isPaginationEventRef.current = true
  }, [currentPage])

  useEffect(() => {
    isFilterChangeEventRef.current = true
    resetClosets()
  }, [filters, selectedDynamicFilters])

  useEffect(() => {
    hasSearchSessionIdChangedRef.current = true
  }, [searchSessionId])

  const fetchClosetPromotions = useCallback(async () => {
    const shouldFetch = !isClosetPromotionKillswitchEnabled && hasMore.current

    if (!shouldFetch) {
      setClosets([])

      return
    }

    const response = await getClosetPromotions({
      excludedUserIds: userIds.current,
      perPage: CLOSET_PROMOTIONS_COUNT,
      screenName: Screen.Catalog,
      searchSessionId,
      selectedDynamicFilters,
      ...filters,
    })

    if ('errors' in response) {
      setClosets([])

      return
    }

    hasMore.current = response.page_info.has_more
    const transformedClosets = transformClosetPromotionsResponse(response)

    userIds.current = [...userIds.current, ...transformedClosets.map(closet => closet.user.id)]
    setClosets(transformedClosets)
  }, [filters, selectedDynamicFilters, isClosetPromotionKillswitchEnabled, searchSessionId])

  useEffect(() => {
    if (uiState !== UiState.Success) return
    if (dynamicFiltersUiState !== UiState.Success) return
    if (!isFilterChangeEventRef.current && !isPaginationEventRef.current) return
    if (isFilterChangeEventRef.current && !hasSearchSessionIdChangedRef.current) return

    fetchClosetPromotions()

    if (isFilterChangeEventRef.current) isFilterChangeEventRef.current = false
    if (isPaginationEventRef.current) isPaginationEventRef.current = false
    hasSearchSessionIdChangedRef.current = false
  }, [
    uiState,
    dynamicFiltersUiState,
    searchSessionId,
    filters,
    selectedDynamicFilters,
    fetchClosetPromotions,
  ])

  const { data: vasEntryPoints } = useFetchVasEntryPoints([VasEntryPoint.PromotedClosets])

  const getClosetListWithBanner = (closetList?: Array<ClosetModel>) => {
    if (!closetList?.length || !closetList) return []

    const closetListClone = [...closetList]

    if (closetListClone[0]) {
      closetListClone[0] = { ...closetListClone[0] }
      closetListClone[0].showBanner = true
    }

    return closetListClone
  }

  const closetsWithBanner = getClosetListWithBanner(closets)

  return { closetsWithBanner, vasEntryPoints }
}

export default useClosetPromotion
